import {Mapper} from '@s-ui/domain'

export default class FromVehicleInfoRawDataToVehicleInfoValueObjectMapper extends Mapper {
  constructor({
    fromImagesListRawDataToImagesListValueObjectMapper,
    fromVersionSpecificationsRawDataToVersionSpecificationsValueObjectMapper,
    fromSearchUrlParamToSearchCodeValueObjectMapper,
    descriptionItemValueObjectFactory,
    electricFeaturesMapper,
    equipmentValueObjectFactory,
    equipmentSectionValueObjectFactory,
    vehicleInfoValueObjectFactory,
    groupedVersionSpecificationsValueObjectFactory,
    versionSpecificationsValueObjectFactory
  }) {
    super()
    this._fromImagesListRawDataToImagesListValueObjectMapper = fromImagesListRawDataToImagesListValueObjectMapper
    this._fromVersionSpecificationsRawDataToVersionSpecificationsValueObjectMapper =
      fromVersionSpecificationsRawDataToVersionSpecificationsValueObjectMapper
    this._fromSearchUrlParamToSearchCodeValueObjectMapper = fromSearchUrlParamToSearchCodeValueObjectMapper
    this._descriptionItemValueObjectFactory = descriptionItemValueObjectFactory
    this._fromElectricFeaturesRawDataToElectricFeaturesVOMapper = electricFeaturesMapper
    this._equipmentValueObjectFactory = equipmentValueObjectFactory
    this._equipmentSectionValueObjectFactory = equipmentSectionValueObjectFactory
    this._vehicleInfoValueObjectFactory = vehicleInfoValueObjectFactory
    this._groupedVersionSpecificationsValueObjectFactory = groupedVersionSpecificationsValueObjectFactory
    this._versionSpecificationsValueObjectFactory = versionSpecificationsValueObjectFactory
    this._grouped = false
    this._FIRST_ITEM = 0
  }

  _parseDate = date => new Date(date).toLocaleDateString()

  _getRawGroupedSpecifications = ({
    bodyType,
    fuelType,
    make,
    model,
    traction,
    transmissionType,
    vehicle,
    versionName
  }) => {
    return {
      body: {
        bodyType: bodyType.name,
        doors: vehicle.numberOfDoors,
        fuelTankCapacity: vehicle.tankCapacityInLiters,
        height: vehicle.dimensionsInMillimeters?.height,
        length: vehicle.dimensionsInMillimeters?.length,
        luggageCapacity: vehicle.trunkCapacityInLiters,
        seats: vehicle.numberOfSeats,
        weight: vehicle.weight,
        width: vehicle.dimensionsInMillimeters?.width
      },
      consumption: {
        acceleration: vehicle.acceleration,
        averageConsumption: vehicle.consumption?.mixed,
        extraUrbanConsumption: vehicle.consumption?.extraUrban,
        topSpeed: vehicle.maxSpeed,
        traction: traction?.name,
        urbanConsumption: vehicle.consumption?.urban
      },
      engine: {
        fuelType: fuelType.name,
        hp: vehicle.horsePower,
        transmissionType: transmissionType.name,
        kw: vehicle.engineDetails?.kw,
        torque: vehicle.engineDetails?.torque,
        supply: vehicle.engineDetails?.supply,
        distribution: vehicle.engineDetails?.distribution,
        cylinders: vehicle.engineDetails?.cylinders,
        cylindersDisposition: vehicle.engineDetails?.cylindersDisposition,
        gears: vehicle.engineDetails?.gears
      },
      initDate: vehicle.start,
      make: make.name,
      makeId: make.makeId,
      model: model.name,
      modelId: make.modelId,
      price: vehicle.manufacturerPrice,
      version: versionName,
      versionId: vehicle.versionId,
      year: vehicle.year
    }
  }

  _getRawSpecifications = ({
    bodyType,
    engineDetails,
    fuelType,
    make,
    model,
    traction,
    transmissionType,
    vehicle,
    versionName
  }) => {
    return {
      acceleration: vehicle.acceleration,
      averageConsumption: vehicle.consumption?.mixed,
      bodyType: bodyType.name,
      cylinders: vehicle.engineDetails?.cylinders,
      cylindersDisposition: vehicle.engineDetails?.cylindersDisposition,
      distribution: vehicle.engineDetails?.distribution,
      doors: vehicle.numberOfDoors,
      extraUrbanConsumption: vehicle.consumption?.extraUrban,
      fuelTankCapacity: vehicle.tankCapacityInLiters,
      fuelType: fuelType.name,
      gears: vehicle.engineDetails?.gears,
      height: vehicle.dimensionsInMillimeters?.height,
      hp: vehicle.horsePower,
      initDate: vehicle.start,
      kw: vehicle.engineDetails?.kw,
      length: vehicle.dimensionsInMillimeters?.length,
      luggageCapacity: vehicle.trunkCapacityInLiters,
      make: make.name,
      makeId: make.makeId,
      model: model.name,
      modelId: make.modelId,
      price: vehicle.manufacturerPrice,
      seats: vehicle.numberOfSeats,
      supply: vehicle.engineDetails?.supply,
      topSpeed: vehicle.maxSpeed,
      torque: vehicle.engineDetails?.torque,
      traction: traction?.name,
      transmissionType: transmissionType.name,
      urbanConsumption: vehicle.consumption?.urban,
      version: versionName,
      versionId: vehicle.versionId,
      weight: vehicle.weight,
      width: vehicle.dimensionsInMillimeters?.width,
      year: vehicle.year
    }
  }

  _getImages = (modelImages, mainPicture) => {
    const hasImages = Boolean(modelImages?.items[this._FIRST_ITEM]?.photos?.length)
    if (!hasImages) return mainPicture ? [{url: mainPicture}] : []
    return modelImages?.items[this._FIRST_ITEM].photos?.map(({photoUrl}) => {
      return {url: photoUrl}
    })
  }

  setParams = ({grouped}) => {
    this._grouped = grouped

    return this
  }

  map = vehicleInfo => {
    const {make, model, modelImages, vehicle} = vehicleInfo

    const {
      start: initDate,
      year,
      makeId,
      price,
      mainPicture,
      vehicleStandardEquipmentGroup: standard,
      vehicleOptionalEquipmentGroup: optional
    } = vehicle

    const vehicleImages = this._getImages(modelImages, mainPicture)

    const {items: images} = this._fromImagesListRawDataToImagesListValueObjectMapper
      .map({
        images: vehicleImages
      })
      .toJSON()
    const rawGroupedSpecifications = this._getRawGroupedSpecifications(vehicleInfo)
    const rawSpecifications = this._getRawSpecifications(vehicleInfo)

    const {
      body,
      consumption: {
        acceleration,
        averageConsumption: consumptionAverage,
        extraUrbanConsumption: consumptionExtraUrban,
        topSpeed,
        traction,
        urbanConsumption: consumptionUrban
      } = {},
      cycle,
      engine,
      isCurrent: isMarketable,
      makeId: makeIdGrouped,
      modelId: modelIdGrouped,
      price: priceGrouped,
      version: versionGrouped,
      year: yearGrouped
    } = rawGroupedSpecifications

    const mappedElectricFeatures = this._fromElectricFeaturesRawDataToElectricFeaturesVOMapper.map({
      electricFeatures: vehicle.electricFeatures
    })

    const groupedSpecifications = this._groupedVersionSpecificationsValueObjectFactory({
      body: this._versionSpecificationsValueObjectFactory({
        ...body,
        version: versionGrouped
      }),
      consumption: this._versionSpecificationsValueObjectFactory({
        acceleration,
        consumptionAverage,
        consumptionExtraUrban,
        consumptionUrban,
        topSpeed,
        traction
      }),
      ...(cycle && {
        cycle: this._versionSpecificationsValueObjectFactory({
          ...cycle,
          ...(mappedElectricFeatures && {
            electricFeatures: mappedElectricFeatures
          })
        })
      }),
      ...(engine && {
        engine: this._versionSpecificationsValueObjectFactory({
          ...engine,
          ...(mappedElectricFeatures && {
            electricFeatures: mappedElectricFeatures
          })
        })
      })
    })

    const specifications = this._versionSpecificationsValueObjectFactory({
      ...rawSpecifications,
      ...(mappedElectricFeatures && {electricFeatures: mappedElectricFeatures}),
      doors: Number(vehicle.numberOfDoors)
    })

    const equipment = this._equipmentValueObjectFactory({
      standard: standard?.map(({description: title, equipment: descriptions}) => {
        return this._equipmentSectionValueObjectFactory({
          title,
          descriptions: descriptions?.map(item => this._descriptionItemValueObjectFactory({value: item}))
        })
      }),
      optional: optional?.map(({description: title, equipment}) =>
        this._equipmentSectionValueObjectFactory({
          title,
          descriptions: equipment?.map(({description}) => this._descriptionItemValueObjectFactory({value: description}))
        })
      )
    })

    const {searchCode: makeCode} = this._fromSearchUrlParamToSearchCodeValueObjectMapper
      .map({
        searchUrlParam: make.name
      })
      .toJSON()

    const {searchCode: modelCode} = this._fromSearchUrlParamToSearchCodeValueObjectMapper
      .map({
        searchUrlParam: model.name
      })
      .toJSON()

    const initMarketedDate = this._parseDate(initDate)

    return this._vehicleInfoValueObjectFactory({
      vehicleInfo: {
        equipment,
        ...(this._grouped && {groupedSpecifications}),
        images,
        ...(initDate && {initMarketedDate}),
        isMarketable,
        make: makeCode,
        makeId: make?.makeId || makeId || makeIdGrouped,
        model: modelCode,
        modelId: model?.modelId || modelIdGrouped,
        price: price || priceGrouped,
        ...(!this._grouped && {specifications}),
        year: year || yearGrouped
      }
    })
  }
}
